import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Receipt, ChevronRight, ChevronDown, ChevronUp, Pencil, FolderOpen,
} from 'lucide-react';
import StatCard from '../shared/StatCard';
import StatusFilterDropdown from '../shared/StatusFilterDropdown';
import DateRangeFilter from '../shared/DateRangeFilter';
import ProjectFilter from '../shared/ProjectFilter';
import ReceiptViewerModal from '../shared/ReceiptViewerModal';
import RoleSummaryTab from '../shared/RoleSummaryTab';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';
import { AMBER, NAVY, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';

const FILTERS = [
  { value: 'all',             label: 'Todos' },
  { value: 'draft',           label: 'Borrador' },
  { value: 'pending',         label: 'Pendiente' },
  { value: 'pending_review',  label: 'Pend. Revisión' },
  { value: 'approved',        label: 'Aprobados' },
  { value: 'paid',            label: 'Pagados' },
  { value: 'rejected',        label: 'Rechazados' },
  { value: 'returned',        label: 'Devueltos' },
];

// Criticality sort: draft → sent_back → partially_returned → pending → partially_approved → pending_review → rejected → approved → paid
const STATUS_SORT = {
  draft: 0, sent_back: 1, partially_returned: 2,
  pending: 3, partially_approved: 4, pending_review: 5, rejected: 6, approved: 7, paid: 8,
};

const SECTION_ORDER = ['draft', 'returned', 'pending', 'pending_review', 'approved', 'paid', 'rejected'];

const SECTION_LABELS = {
  draft:          'Borradores',
  returned:       'Devueltos',
  pending:        'En Revisión',
  pending_review: 'Pendiente de Revisión',
  approved:       'Aprobados',
  paid:           'Pagados',
  rejected:       'Rechazados',
};

function SectionHeader({ section, count, expanded, onToggle }) {
  const colorKey = section === 'returned' ? 'sent_back' : section;
  const s = STATUS_COLORS[colorKey] || STATUS_COLORS.pending;
  const Icon = expanded ? ChevronUp : ChevronDown;
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        background: '#fff',
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: expanded ? '12px 12px 0 0' : 12,
        cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        minHeight: 48,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: s.border, flexShrink: 0, display: 'inline-block',
        }} />
        <span style={{ fontWeight: '600', fontSize: 14, color: NAVY }}>
          {SECTION_LABELS[section] || section}
        </span>
      </div>
      <span style={{
        background: '#f1f5f9', color: '#64748b',
        borderRadius: 20, padding: '2px 10px',
        fontSize: 12, fontWeight: '500',
        marginRight: 12,
      }}>
        {count} recibo{count !== 1 ? 's' : ''}
      </span>
      <Icon size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
    </button>
  );
}

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function ItemStatusBadge({ status }) {
  if (!status) return null;
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: '600',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function HistoryPage({ receipts, onNewExpense, onResumeDraft, onEditSentBack, onViewReceipt, onRefresh }) {
  const [filter,   setFilter]   = useState(new Set());
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo,   setDateTo]   = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [sectionExpanded, setSectionExpanded] = useState({
    draft: true, returned: true, pending: true, pending_review: true, approved: false, paid: false, rejected: false,
  });

  useEffect(() => { onRefresh({}); }, [onRefresh]);

  // Derive unique project names from all receipts
  const allProjectNames = useMemo(() => {
    const names = new Set();
    receipts.forEach(r => (r.items || []).forEach(i => { if (i.projectName) names.add(i.projectName); }));
    return [...names].sort();
  }, [receipts]);

  // Helper: check if a receipt matches the selected projects filter
  function matchesProjectFilter(r) {
    if (selectedProjects.length === 0) return true;
    return (r.items || []).some(i => selectedProjects.includes(i.projectName));
  }

  // Stats — Total counts ALL receipts regardless of status
  const total       = receipts.length;
  const drafts      = receipts.filter((r) => r.status === 'draft');
  const pendingGrp  = receipts.filter((r) => ['pending', 'partially_approved'].includes(r.status));
  const returnedGrp = receipts.filter((r) => ['sent_back', 'partially_returned'].includes(r.status));
  const approved    = receipts.filter((r) => r.status === 'approved');
  const rejected    = receipts.filter((r) => r.status === 'rejected');
  const approvedAmt = approved.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // Filter mapping for compound sections (multi-select)
  const filtered = receipts.filter((r) => {
    if (filter.size > 0) {
      // Build effective set including grouped statuses
      const match = filter.has(r.status)
        || (filter.has('pending') && r.status === 'partially_approved')
        || (filter.has('returned') && r.status === 'partially_returned')
        || (filter.has('returned') && r.status === 'sent_back');
      if (!match) return false;
    }
    if (dateFrom && r.date && r.date < dateFrom) return false;
    if (dateTo   && r.date && r.date > dateTo)   return false;
    if (!matchesProjectFilter(r)) return false;
    return true;
  });

  // Sort by criticality
  const sorted = [...filtered].sort(
    (a, b) => (STATUS_SORT[a.status] ?? 99) - (STATUS_SORT[b.status] ?? 99)
  );

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    setDateFrom(df);
    setDateTo(dt);
    onRefresh({ dateFrom: df, dateTo: dt });
  }

  function toggleExpand(e, id) {
    e.stopPropagation();
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function handleCardClick(r) {
    if (r.status === 'draft') {
      if (onViewReceipt) onViewReceipt(r);
      else onResumeDraft();
    } else if (onViewReceipt) onViewReceipt(r);
  }

  function toggleSection(section) {
    setSectionExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  }

  function getReceiptsForSection(section) {
    const dateFiltered = receipts.filter((r) => {
      if (dateFrom && r.date && r.date < dateFrom) return false;
      if (dateTo   && r.date && r.date > dateTo)   return false;
      if (!matchesProjectFilter(r)) return false;
      return true;
    });
    if (section === 'pending') {
      return dateFiltered.filter(r => ['pending', 'partially_approved'].includes(r.status));
    }
    if (section === 'returned') {
      return dateFiltered.filter(r => ['sent_back', 'partially_returned'].includes(r.status));
    }
    return dateFiltered.filter(r => r.status === section);
  }

  const isClickable = () => true;

  const itemsExpandKey = (id) => `items_${id}`;
  const feedbackExpandKey = (id) => `feedback_${id}`;

  const renderCardContent = (r) => {
    const items = r.items || [];
    const isItemsOpen = expanded[itemsExpandKey(r.id)];
    const isFeedbackOpen = expanded[feedbackExpandKey(r.id)];

    // Collect any item-level feedback (for sent_back / rejected items)
    const itemsWithFeedback = items.filter(it =>
      (it.status === 'sent_back' || it.status === 'rejected') && (it.reviewReason || it.reviewNotes)
    );
    // Also check receipt-level feedback
    const hasReceiptFeedback = ['sent_back', 'partially_returned', 'rejected'].includes(r.status) && r.reviewReason;
    const hasFeedback = hasReceiptFeedback || itemsWithFeedback.length > 0;

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>

          {/* Preview thumbnail — click to view full size */}
          <div
            onClick={(e) => { e.stopPropagation(); if (r.previewUrl) setViewingReceipt(r); }}
            title={r.previewUrl ? 'Ver recibo en grande' : ''}
            style={{
              width: 46, height: 46, borderRadius: 8, overflow: 'hidden',
              flexShrink: 0, background: '#f1f5f9',
              border: `1px solid ${BORDER_COLOR}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: r.previewUrl ? 'pointer' : 'default',
            }}>
            {r.previewUrl && r.fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={r.previewUrl} fileType={r.fileType} alt="receipt" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <Receipt size={20} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Main info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Primary header: Receipt# — Vendor | Date | Payment | Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 5 }}>
              <span style={{ fontWeight: '700', fontSize: 15, color: '#1e293b' }}>
                {r.receiptNumber ? `#${r.receiptNumber}` : '(Sin #)'}
                {r.invoiceNumber ? ` · Factura: ${r.invoiceNumber}` : ''}
                {' — '}
                {r.vendor || '(Proveedor desconocido)'}
              </span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>{r.date || '—'}</span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {r.paymentMethod ? (PAYMENT_METHODS[r.paymentMethod] || r.paymentMethod) : '—'}
              </span>
              <StatusBadge status={r.displayStatus || r.status} />
            </div>

            {/* Project names line */}
            {items.length > 0 && (() => {
              const projectNames = [...new Set(items.map(i => i.projectName).filter(Boolean))];
              return projectNames.length > 0 ? (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  Proyectos: {projectNames.join(', ')}
                </div>
              ) : null;
            })()}

            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {r.fileName} · {r.status === 'draft'
                ? `Guardado el ${new Date(r.uploadedAt).toLocaleDateString('es-PR')}`
                : `Enviado el ${new Date(r.uploadedAt).toLocaleDateString('es-PR')}`}
            </div>

            {/* Expandable line items toggle */}
            {items.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={(e) => toggleExpand(e, itemsExpandKey(r.id))}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: '#475569', fontWeight: '600', padding: 0,
                    fontFamily: 'Inter, sans-serif',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  {isItemsOpen
                    ? <ChevronDown size={13} />
                    : <ChevronRight size={13} />
                  }
                  Ver artículos ({items.length})
                </button>
              </div>
            )}

            {/* Feedback indicator (feedback now shown inline with items) */}
            {hasFeedback && !isItemsOpen && (
              <div style={{ marginTop: 6, fontSize: 11, color: AMBER, fontWeight: 600 }}>
                Tiene comentarios del supervisor — expandir items para ver
              </div>
            )}
          </div>

          {/* Amount + action */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontWeight: '700', fontSize: 17, color: '#1e293b', marginBottom: 8 }}>
              {fmt(r.amount || (r.items && r.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)))}
            </div>
            {(r.status === 'sent_back' || r.status === 'partially_returned') && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewReceipt ? onViewReceipt(r) : onEditSentBack(r); }}
                style={{
                  background: AMBER, color: '#fff',
                  border: 'none', borderRadius: 8,
                  padding: '7px 12px', fontWeight: '600',
                  fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 5,
                  whiteSpace: 'nowrap',
                }}
              >
                <Pencil size={12} /> Editar
              </button>
            )}
            {r.status === 'draft' && (
              <button
                onClick={(e) => { e.stopPropagation(); onResumeDraft(); }}
                style={{
                  background: '#f8fafc', color: '#475569',
                  border: `1px solid ${BORDER_COLOR}`, borderRadius: 8,
                  padding: '7px 12px', fontWeight: '600',
                  fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex', alignItems: 'center', gap: 5,
                  whiteSpace: 'nowrap',
                }}
              >
                <FolderOpen size={12} /> Abrir
              </button>
            )}
          </div>
        </div>

        {/* Expandable line items — grouped by project with inline feedback */}
        {isItemsOpen && items.length > 0 && (() => {
          const itemsByProject = {};
          for (const item of items) {
            const key = item.projectName || '(Sin Proyecto)';
            if (!itemsByProject[key]) itemsByProject[key] = [];
            itemsByProject[key].push(item);
          }
          const projectGroups = Object.entries(itemsByProject);

          return (
            <div style={{
              marginTop: 10,
              background: '#f8fafc',
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 8,
              overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#64748b', fontWeight: '600' }}>Descripción</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#64748b', fontWeight: '600', width: 90 }}>Monto</th>
                    <th style={{ textAlign: 'center', padding: '4px 8px', color: '#64748b', fontWeight: '600', width: 90 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {projectGroups.map(([projectName, projectItems]) => {
                    const supervisorName = projectItems[0]?.supervisorName || '—';
                    const sectionTotal = projectItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
                    // Deduplicated feedback: one reason per supervisor section
                    const sectionFeedback = projectItems.find(i => i.reviewReason && (i.status === 'sent_back' || i.status === 'rejected'));
                    return (
                    <Fragment key={projectName}>
                      <tr>
                        <td colSpan={3} style={{
                          padding: '6px 14px', background: '#f0f4ff',
                          fontWeight: 600, fontSize: 12, color: NAVY,
                          borderBottom: `1px solid ${BORDER_COLOR}`,
                        }}>
                          {projectName}
                          <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>Supervisor: {supervisorName}</span>
                          <span style={{ float: 'right', fontWeight: 700 }}>{fmt(sectionTotal)}</span>
                        </td>
                      </tr>
                      {projectItems.map((item, idx) => (
                        <tr key={item.id || idx} style={{ borderBottom: idx < projectItems.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                          <td style={{ padding: '6px 8px', color: '#1e293b' }}>{item.description || '—'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>{fmt(item.amount)}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                            <ItemStatusBadge status={item.status} />
                            {item.isPaid && (
                              <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 12, padding: '1px 7px', fontSize: 9, fontWeight: 600, whiteSpace: 'nowrap' }}>Pagado</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {/* Inline supervisor feedback for this section */}
                      {sectionFeedback && (
                        <tr>
                          <td colSpan={3} style={{
                            padding: '8px 14px',
                            background: sectionFeedback.status === 'sent_back' ? '#fffbeb' : '#fef2f2',
                            borderBottom: `1px solid ${sectionFeedback.status === 'sent_back' ? '#fcd34d' : '#fecaca'}`,
                            fontSize: 12,
                          }}>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>{sectionFeedback.reviewReason}</span>
                            {sectionFeedback.reviewNotes && (
                              <span style={{ color: '#64748b', fontStyle: 'italic', marginLeft: 8 }}>{sectionFeedback.reviewNotes}</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>Mis Reportes de Gastos</h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {total} recibo{total !== 1 ? 's' : ''} total
          </div>
        </div>
        <div className="desktop-only" style={{ display: 'flex', gap: 10 }}>
          {drafts.length > 0 && (
            <button
              onClick={onResumeDraft}
              style={{
                background: '#fff', color: '#475569', border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 8, padding: '8px 16px', fontWeight: '600',
                fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <FolderOpen size={15} /> Borradores
            </button>
          )}
          <button
            onClick={onNewExpense}
            style={{
              background: AMBER, color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 16px', fontWeight: '600',
              fontSize: 13, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            + Nuevo Gasto
          </button>
        </div>
      </div>

      {/* Stat Cards — Total, Drafts, Pending (incl. sent back), Approved, Rejected */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total"       value={total}              onClick={() => setFilter(new Set())}              active={filter.size === 0} />
        <StatCard label="Borradores"  value={drafts.length}      onClick={() => setFilter(new Set(['draft']))}     active={filter.size === 1 && filter.has('draft')} />
        <StatCard label="En Revisión" value={pendingGrp.length}  onClick={() => setFilter(new Set(['pending']))}   active={filter.size === 1 && filter.has('pending')} />
        <StatCard label="Devueltos"   value={returnedGrp.length} onClick={() => setFilter(new Set(['returned']))}  active={filter.size === 1 && filter.has('returned')} />
        <StatCard label="Aprobados"   value={approved.length}    subtitle={fmt(approvedAmt)} subtitleLarge highlight onClick={() => setFilter(new Set(['approved']))} active={filter.size === 1 && filter.has('approved')} />
        <StatCard label="Rechazados"  value={rejected.length}    onClick={() => setFilter(new Set(['rejected']))}  active={filter.size === 1 && filter.has('rejected')} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusFilterDropdown options={FILTERS} active={filter} onChange={setFilter} />
        <div className="date-range" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
        </div>
        <ProjectFilter projects={allProjectNames} selected={selectedProjects} onChange={setSelectedProjects} />
      </div>

      {/* Receipt Cards */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <Receipt size={44} style={{ marginBottom: 12, color: '#cbd5e1' }} />
          <div style={{ fontSize: 16, fontWeight: '600', marginBottom: 6, color: '#64748b' }}>
            Aún no hay reportes de gastos
          </div>
          <div style={{ fontSize: 13 }}>Toca "+ Nuevo Gasto" para comenzar.</div>
        </div>
      ) : filter.size === 0 ? (
        /* ── Grouped view (sections) ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {SECTION_ORDER.map(section => {
            const sectionReceipts = getReceiptsForSection(section);
            if (sectionReceipts.length === 0) return null;
            const isOpen = sectionExpanded[section];
            return (
              <div key={section}>
                <SectionHeader
                  section={section}
                  count={sectionReceipts.length}
                  expanded={isOpen}
                  onToggle={() => toggleSection(section)}
                />
                {isOpen && (
                  <div style={{
                    border: `1px solid ${BORDER_COLOR}`,
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    overflow: 'hidden',
                    background: '#fff',
                    display: 'flex', flexDirection: 'column', gap: 0,
                  }}>
                    {sectionReceipts.map((r) => (
                      <div
                        key={r.id}
                        onClick={() => handleCardClick(r)}
                        style={{
                          padding: '14px 18px',
                          cursor: isClickable(r) ? 'pointer' : 'default',
                          borderBottom: `1px solid #f1f5f9`,
                          background: '#fff',
                        }}
                        onMouseEnter={(e) => {
                          if (isClickable(r)) e.currentTarget.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (isClickable(r)) e.currentTarget.style.background = '#fff';
                        }}
                      >
                        {renderCardContent(r)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Flat view (filtered) ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map((r) => (
            <div
              key={r.id}
              onClick={() => handleCardClick(r)}
              style={{
                ...bubbleStyle,
                padding: '14px 18px',
                cursor: isClickable(r) ? 'pointer' : 'default',
                transition: 'box-shadow 0.15s, transform 0.1s',
              }}
              onMouseEnter={(e) => {
                if (isClickable(r)) {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable(r)) {
                  e.currentTarget.style.boxShadow = '';
                  e.currentTarget.style.transform = '';
                }
              }}
            >
              {renderCardContent(r)}
            </div>
          ))}
        </div>
      )}

      {/* Receipt full-size viewer modal */}
      {viewingReceipt && (
        <ReceiptViewerModal
          receipt={{
            id: viewingReceipt.id,
            vendor: viewingReceipt.vendor,
            employeeName: '',
            status: viewingReceipt.status,
            amount: viewingReceipt.amount,
            date: viewingReceipt.date,
            fileName: viewingReceipt.fileName,
            fileType: viewingReceipt.fileType,
            previewUrl: viewingReceipt.previewUrl,
          }}
          hasPrev={false}
          hasNext={false}
          onPrev={() => {}}
          onNext={() => {}}
          onClose={() => setViewingReceipt(null)}
        />
      )}
    </div>
  );
}
